<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-10" @close="closeModal">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="activeInfoTooltip ? 'shadow' : ''"
              @click="hideTooltips"
              class="
                w-full
                max-w-md
                transform
                overflow-hidden
                rounded-xl
                bg-white
                text-left
                align-middle
                shadow-xl
                transition-all
              "
            >
              <DialogTitle
                as="div"
                class="
                  p-5
                  md
                  font-bold
                  container
                  inline-flex
                  justify-between
                  items-center
                  leading-6
                  col-md-6
                  rounded-xl rounded-b-none
                "
                style="border-bottom: 1px solid #e4e4e4; color: #3eb7a6; background-color: #f6f8f7"
              >
                <h2 class="block col-md-6 text-left text-lg text-vice-teal-900">
                  Let's check everything is working
                </h2>
                <img
                  @click="toggleTooltip('info')"
                  class="h-6 relative gray ml-1 mt-1 block col-md-6 text-right"
                  src="/image/icons/info.svg"
                  alt="info"
                  ref="infoTooltip"
                />
                <div class="tooltip-info max-w-sm mx-3 my-8 px-3 py-3" v-if="activeInfoTooltip">
                  <p class="tooltip-text-lh mb-2">
                    To ensure high quality VCE sessions, please follow these minimum requirements:
                  </p>
                  <p>
                    <b>Download speed:</b>
                    20 Mbps or up
                  </p>
                  <p>
                    <b>Upload speed:</b>
                    {{ isClinician ? '20' : '15' }} Mbps or up
                  </p>
                  <p>
                    <b>Browser:</b>
                    Google Chrome or Microsoft Edge
                  </p>
                  <p>
                    <b>Minimum screen size:</b>
                  </p>
                  <ul>
                    <li class="ml-2">Desktop 1280×720</li>
                    <li class="ml-2">Tablet 601×962</li>
                  </ul>
                  <p class="mb-2">
                    <b>Operating system:</b>
                    MacOS / Windows 10 and up
                  </p>
                  <p v-if="isClinician" class="tooltip-text-lh">
                    <b>Tip:</b>
                    When you do not meet these requirements, please discuss with your IT
                    representative or contact the VCE support team via
                    <a href="mailto:support@gheg.org"><b>support@gheg.org</b></a>
                  </p>
                  <p v-else class="tooltip-text-lh">
                    <b>Tip:</b>
                    When you do not meet these requirements, please discuss with your
                    clinician/teacher.
                  </p>
                </div>
              </DialogTitle>
              <DialogOverlay class="p-5 pt-0">
                <div class="container inline-flex justify-between text-sm">
                  <div class="block col-md-6 font-medium text-gray-700">
                    <ul ref="liItems">
                      <li class="mt-4 mb-4 flex items-center" :key="0" ref="liIcon">
                        <CheckRequirementsNetwork
                          @click="toggleTooltip(0)"
                          class="relative mr-1"
                          :isSuccess="speedCheckPassed(downloadSpeed)"
                        ></CheckRequirementsNetwork>
                        Download speed (approx)
                        <p
                          class="text-red-600 error-tip"
                          v-if="!speedCheckPassed(downloadSpeed) && downloadSpeed"
                        >
                          Your download speed is too low
                        </p>
                        <div class="tooltip" v-if="activeTooltip === 0">20 Mbps or up</div>
                      </li>
                      <li class="mt-4 mb-4 flex items-center" :key="1" ref="liIcon">
                        <CheckRequirementsNetwork
                          @click="toggleTooltip(1)"
                          class="relative mr-1"
                          :isSuccess="speedCheckPassed(uploadSpeed, 'upload')"
                        ></CheckRequirementsNetwork>
                        Upload speed (approx)
                        <p
                          class="text-red-600 error-tip"
                          v-if="!speedCheckPassed(uploadSpeed, 'upload') && uploadSpeed"
                        >
                          Your upload speed is too low
                        </p>
                        <div class="tooltip" v-if="activeTooltip === 1">
                          {{ isClinician ? '20' : '15' }} Mbps or up
                        </div>
                      </li>
                      <li class="mt-4 mb-4 flex items-center" :key="2" ref="liIcon">
                        <CheckRequirementsBrowser
                          @click="toggleTooltip(2)"
                          class="relative mr-1"
                          :isSuccess="this.browserCheck"
                        ></CheckRequirementsBrowser>
                        Browser
                        <p
                          class="text-red-600 error-tip"
                          style="margin-top: 2.7rem"
                          v-if="!this.browserCheck"
                        >
                          Your browser is not supported.
                          <br />
                          Please download the latest version of
                          <a
                            href="https://www.google.com/intl/en/chrome/"
                            target="_blank"
                            class="outline-none text-blue-600 dark:text-blue-500 hover:underline"
                          >
                            Chrome
                          </a>
                          /
                          <a
                            href="https://www.microsoft.com/en-us/edge"
                            target="_blank"
                            class="outline-none text-blue-600 dark:text-blue-500 hover:underline"
                          >
                            Edge
                          </a>
                        </p>
                        <div class="tooltip" v-if="activeTooltip === 2">
                          Google Chrome or Microsoft Edge
                        </div>
                      </li>
                      <li class="mt-4 mb-4 flex items-center" :key="3" ref="liIcon">
                        <CheckRequirementsMonitor
                          @click="toggleTooltip(3)"
                          class="relative mr-1"
                          :isSuccess="resolutionCheckPassed()"
                        ></CheckRequirementsMonitor>
                        Screen window size
                        <p
                          class="text-red-600 error-tip"
                          style="margin-top: 2.7rem"
                          v-if="!resolutionCheckPassed()"
                        >
                          Your window size is too small.
                          <br />
                          Please zoom out in your browser settings
                        </p>
                        <div class="tooltip" v-if="activeTooltip === 3">
                          Desktop 1280×720; Tablet 601×962
                        </div>
                      </li>
                      <li class="mt-4 mb-4 flex items-center" :key="4" ref="liIcon">
                        <CheckRequirementsMonitor
                          @click="toggleTooltip(4)"
                          class="relative mr-1"
                          :isSuccess="this.checkOS"
                        ></CheckRequirementsMonitor>
                        Operating system
                        <p
                          class="text-red-600 error-tip"
                          style="margin-top: 2.7rem"
                          v-if="!this.checkOS"
                        >
                          Your current hardware is not supported.
                          <br />
                          Please upgrade your operating system
                        </p>
                        <div class="tooltip" v-if="activeTooltip === 4">
                          MacOS / Windows 10 and up
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div class="block col-md-6 text-right text-gray-600">
                    <ul>
                      <li class="mt-6 mb-4">
                        <span v-if="downloadSpeed">
                          {{ `${downloadSpeed} Mbps` }}
                        </span>
                        <span v-else class="inline-flex">
                          <svg
                            class="animate-spin -ml-1 mr-5 h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="#14B8A6"
                              stroke-width="4"
                            ></circle>
                            <path
                              fill="#14B8A6"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </span>
                      </li>
                      <li class="mt-2 mb-4 li-measured">
                        <span v-if="uploadSpeed">
                          {{ `${uploadSpeed} Mbps` }}
                        </span>
                        <span v-else class="inline-flex">
                          <svg
                            class="animate-spin -ml-1 mr-5 h-3 w-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="#14B8A6"
                              stroke-width="4"
                            ></circle>
                            <path
                              fill="#14B8A6"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </span>
                      </li>
                      <li class="mt-2 mb-2 li-measured">{{ getBrowserName() }}</li>
                      <li class="mt-2 mb-4 li-measured">{{ windowWidth }}x{{ windowHeight }}</li>
                      <li class="mt-2 mb-4 li-measured">{{ userOS }}</li>
                    </ul>
                  </div>
                </div>
                <div class="container inline-block justify-content-center mt-2 mb-2">
                  <ParticipantCameraTest></ParticipantCameraTest>
                </div>
                <div class="container inline-block justify-content-center mt-2">
                  <ParticipantMicroTest></ParticipantMicroTest>
                </div>
              </DialogOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import ParticipantMicroTest from '@/Components/Devices/ParticipantMicroTest.vue';
import ParticipantCameraTest from '@/Components/Devices/ParticipantCameraTest.vue';
import { APP_URL, TEST_IMAGE_CDN_URL, TEST_IMAGE_SIZE } from '@/Utils/vice-constants';
import CheckRequirementsNetwork from '@/Components/Icons/CheckRequirementsNetwork.vue';
import CheckRequirementsBrowser from '@/Components/Icons/CheckRequirementsBrowser.vue';
import CheckRequirementsMonitor from '@/Components/Icons/CheckRequirementsMonitor.vue';

export default {
  name: 'SystemCheckModal',
  props: {
    isClinician: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CheckRequirementsMonitor,
    CheckRequirementsBrowser,
    CheckRequirementsNetwork,
    ParticipantMicroTest,
    ParticipantCameraTest,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogOverlay,
  },
  data() {
    return {
      isOpen: true,
      browserName: null,
      userOS: null,
      windowWidth: null,
      windowHeight: null,
      downloadSpeed: null,
      uploadSpeed: null,
      activeTooltip: null,
      activeInfoTooltip: false,
      checkOS: false,
      browserCheck: false,
    };
  },
  mounted() {
    this.checkSystem();
  },
  emits: ['togglePlatformRequirementsModal'],
  methods: {
    toggleTooltip(index) {
      if (this.activeTooltip === index) {
        this.activeTooltip = null;
      } else {
        this.activeTooltip = index;
      }

      if (index === 'info') {
        this.activeInfoTooltip = !this.activeInfoTooltip;
      }
    },

    hideTooltips(event) {
      const liItems = this.$refs.liItems;
      if (!liItems.contains(event.target) && !this.activeInfoTooltip) {
        this.activeTooltip = null;
      }

      const infoTooltip = this.$refs.infoTooltip;
      if (!infoTooltip.contains(event.target) && this.activeInfoTooltip) {
        this.activeInfoTooltip = null;
      }
    },

    async checkSystem() {
      this.getWindowSize();
      this.browserName = this.getBrowserName();
      this.userOS = this.getUserOS();
      await this.checkDownloadSpeed();
      await this.checkUploadSpeed();
    },

    closeModal() {
      this.$emit('togglePlatformRequirementsModal', false);
    },

    getWindowSize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;

      window.addEventListener('resize', this.updateWindowSize);
    },

    async checkUploadSpeed() {
      const http = new XMLHttpRequest();
      let endTime;
      const uploadUrl = `${APP_URL}/check-upload`;
      const data = `d=${'x'.repeat(10 * 1024 * 1024)}`;

      http.open('POST', uploadUrl, true);
      http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
      http.onreadystatechange = () => {
        if (http.readyState === 4 && http.status === 200) {
          endTime = performance.now();
          this.uploadSpeed = getResult();
        }
      };

      const startTime = performance.now();
      http.send(data);

      const getResult = () => {
        const duration = (endTime - startTime) / 1000;
        const bitsLoaded = data.length * 8;

        return (bitsLoaded / duration / 1000000).toFixed(2);
      };
    },

    async checkDownloadSpeed() {
      let endTime;
      const download = new Image();
      const downloadSize = TEST_IMAGE_SIZE;
      const downloadUrl = TEST_IMAGE_CDN_URL;

      download.onload = () => {
        endTime = performance.now();
        this.downloadSpeed = getResult();
      };

      const startTime = performance.now();
      download.src = `${downloadUrl}?no-cache=${startTime}`;

      const getResult = () => {
        const duration = (endTime - startTime) / 1000;
        const bitsLoaded = downloadSize * 8;

        return (bitsLoaded / duration / 1000 / 1000).toFixed(2);
      };
    },
    speedCheckPassed(speed, type = 'download') {
      let minSpeed = 20;
      if (type === 'upload' && !this.isClinician) {
        minSpeed = 15;
      }

      return speed > minSpeed;
    },
    resolutionCheckPassed() {
      let minWidth = 1280;
      let minHeight = 720;

      // assume that it's tablet
      if ('ontouchstart' in window || navigator.msMaxTouchPoints > 0) {
        minWidth = 601;
        minHeight = 962;
      }

      return !(this.windowWidth < minWidth || this.windowHeight < minHeight);
    },
    updateWindowSize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
    getBrowserName() {
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown';

      if (userAgent.indexOf('Firefox') !== -1 || userAgent.indexOf('FxiOS') !== -1) {
        browserName = 'Mozilla Firefox';
      } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
        browserName = 'Opera';
      } else if (userAgent.indexOf('Edge') !== -1 || userAgent.indexOf('Edg') !== -1) {
        this.browserCheck = true;
        browserName = 'Microsoft Edge';
      } else if (userAgent.indexOf('Chrome') !== -1 || userAgent.indexOf('CriOS') !== -1) {
        this.browserCheck = true;
        browserName = 'Google Chrome';
      } else if (userAgent.indexOf('Safari') !== -1) {
        browserName = 'Apple Safari';
      } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
        browserName = 'Internet Explorer';
      }
      return browserName;
    },
    getUserOS() {
      const userAgent = navigator.userAgent;
      let userOS = 'Unknown';
      this.checkOS = false;

      if (userAgent.indexOf('Win') !== -1) {
        const windowsVersion = parseFloat(userAgent.match(/Windows NT (\d+\.\d+)/)?.[1]) ?? '';
        if (windowsVersion && windowsVersion >= 10) {
          this.checkOS = true;
        }
        userOS = `Windows ${Number.isNaN(windowsVersion) ? '' : windowsVersion}`;
      } else if (userAgent.indexOf('Android') !== -1) {
        userOS = 'Android';
      } else if (userAgent.indexOf('Linux') !== -1) {
        userOS = 'Linux';
      } else if (userAgent.indexOf('(iPhone') !== -1 || userAgent.indexOf('(iPad') !== -1) {
        userOS = 'Mobile iOS';
      } else if (userAgent.indexOf('Mac') !== -1) {
        this.checkOS = true;
        userOS = 'MacOS';
      } else if (userAgent.indexOf('BlackBerry') !== -1 || userAgent.indexOf('PlayBook') !== -1) {
        userOS = 'BlackBerry';
      }

      return userOS;
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowSize);
  },
};
</script>

<style scoped>
.device-label {
  color: gray;
  font-size: smaller;
  margin-left: 4px;
}

.tooltip {
  position: absolute;
  background: #262626;
  color: white;
  font-size: smaller;
  border-radius: 0 5px 5px 5px;
  padding-left: 5px;
  padding-right: 5px;
  margin-top: 2.5rem;
  margin-left: 2.5rem;
}

.tooltip-info {
  position: absolute;
  top: 1rem;
  background: #262626;
  color: #ededed;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 5px;
  z-index: 1;
}

.tooltip-info ul {
  list-style: inside;
  line-height: 1.3rem;
}

.tooltip-info-sub {
  list-style: none !important;
}

.li-measured {
  margin-top: 1.9rem;
}

.shadow {
  background-color: rgba(238, 238, 238, 100);
}

.tooltip-text-lh {
  line-height: 1.6;
}

.error-tip {
  line-height: 0.8rem;
  position: absolute;
  margin: 2rem 0 0 2.5rem;
  font-size: smaller;
}
</style>
